import type { AssertTrue, IsNever } from "conditional-type-checks";
import { difference } from "lodash";
import type { WebhookTopic } from "shopify-api-node";
import { getShopifyModelApiIdentifierByModelKey } from "./apiIdentifiers";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { shopifyModelMetadataForVersion } from "./ShopifyModelMetadata";
import { WebhookApiUpgradeItem } from "./utils";

// This list combines topics listed by shopify-api-node as well as any others we subscribe to
export type ShopifyWebhookTopic =
  | WebhookTopic
  | (
      | "app_purchases_one_time/update"
      | "app_subscriptions/update"
      | "customer_payment_methods/create"
      | "customer_payment_methods/revoke"
      | "customer_payment_methods/update"
      | "customers/data_request"
      | "customers/redact"
      | "disputes/create"
      | "disputes/update"
      | "domains/create"
      | "domains/update"
      | "domains/destroy"
      | "locations/activate"
      | "locations/deactivate"
      | "scheduled_product_listings/add"
      | "scheduled_product_listings/remove"
      | "scheduled_product_listings/update"
      | "shop/redact"
      | "fulfillment_orders/order_routing_complete"
      | "fulfillment_orders/fulfillment_request_submitted"
      | "fulfillment_orders/fulfillment_request_accepted"
      | "fulfillment_orders/fulfillment_request_rejected"
      | "fulfillment_orders/placed_on_hold"
      | "fulfillment_orders/cancellation_request_accepted"
      | "fulfillment_orders/cancellation_request_rejected"
      | "fulfillment_orders/cancelled"
      | "fulfillment_orders/fulfillment_service_failed_to_complete"
      | "fulfillment_orders/moved"
      | "fulfillment_orders/split"
      | "fulfillment_orders/merged"
      | "fulfillment_orders/cancellation_request_submitted"
      | "fulfillment_orders/hold_released"
      | "fulfillment_orders/line_items_prepared_for_local_delivery"
      | "fulfillment_orders/line_items_prepared_for_pickup"
      | "fulfillment_orders/rescheduled"
      | "fulfillment_orders/scheduled_fulfillment_order_ready"
      | "markets/create"
      | "markets/update"
      | "markets/delete"
      | "companies/create"
      | "companies/update"
      | "companies/delete"
      | "company_contacts/create"
      | "company_contacts/update"
      | "company_contacts/delete"
      | "company_locations/create"
      | "company_locations/update"
      | "company_locations/delete"
      | "discounts/create"
      | "discounts/update"
      | "discounts/delete"
      | "orders/risk_assessment_changed"
      | "channels/delete"
      | "collection_publications/create"
      | "collection_publications/delete"
      | "collection_publications/update"
      | "customer.tags_added"
      | "customer.tags_removed"
      | "attributed_sessions/first"
      | "attributed_sessions/last"
      | "product_publications/create"
      | "product_publications/delete"
      | "product_publications/update"
      | "segments/create"
      | "segments/delete"
      | "segments/update"
      | "shipping_addresses/create"
      | "shipping_addresses/update"
      | "tax_partners/update"
      | "tax_services/create"
      | "tax_services/update"
      | "variants/in_stock"
      | "variants/out_of_stock"
      | "app_subscriptions/approaching_capped_amount"
      | "returns/cancel"
      | "returns/close"
      | "returns/reopen"
      | "returns/request"
      | "returns/approve"
      | "returns/update"
      | "returns/decline"
      | "reverse_deliveries/attach_deliverable"
      | "reverse_fulfillment_orders/dispose"
      | "payment_terms/create"
      | "payment_terms/delete"
      | "payment_terms/update"
      | "product_feeds/create"
      | "product_feeds/update"
      | "orders/shopify_protect_eligibility_changed"
      | "publications/delete"
      | "audit_events/admin_api_activity"
      | "subscription_contracts/activate"
      | "subscription_contracts/pause"
      | "subscription_contracts/cancel"
      | "subscription_contracts/fail"
      | "subscription_contracts/expire"
      | "subscription_billing_cycles/skip"
      | "subscription_billing_cycles/unskip"
      | "metaobjects/create"
      | "metaobjects/update"
      | "metaobjects/delete"
      | "discounts/redeemcode_added"
      | "discounts/redeemcode_removed"
      | "metafield_definitions/create"
      | "metafield_definitions/delete"
      | "metafield_definitions/update"
      | "app/scopes_update"
      | "customers/purchasing_summary"
      | "customer.joined_segment"
      | "customer.left_segment"
    );

export const SHOPIFY_WEBHOOKS = {
  "2021-10": [
    "app/uninstalled",
    "app_purchases_one_time/update",
    "app_subscriptions/update",
    "bulk_operations/finish",
    "carts/create",
    "carts/update",
    "checkouts/create",
    "checkouts/delete",
    "checkouts/update",
    "collection_listings/add",
    "collection_listings/remove",
    "collection_listings/update",
    "collections/create",
    "collections/delete",
    "collections/update",
    "customer_groups/create",
    "customer_groups/delete",
    "customer_groups/update",
    "customer_payment_methods/create",
    "customer_payment_methods/revoke",
    "customer_payment_methods/update",
    "customers/create",
    "customers/data_request",
    "customers/delete",
    "customers/disable",
    "customers/enable",
    "customers/redact",
    "customers/update",
    "customers_marketing_consent/update",
    "disputes/create",
    "disputes/update",
    "domains/create",
    "domains/destroy",
    "domains/update",
    "draft_orders/create",
    "draft_orders/delete",
    "draft_orders/update",
    "fulfillment_events/create",
    "fulfillment_events/delete",
    "fulfillments/create",
    "fulfillments/update",
    "inventory_items/create",
    "inventory_items/delete",
    "inventory_items/update",
    "inventory_levels/connect",
    "inventory_levels/disconnect",
    "inventory_levels/update",
    "locations/create",
    "locations/delete",
    "locations/update",
    "order_transactions/create",
    "orders/cancelled",
    "orders/create",
    "orders/delete",
    "orders/edited",
    "orders/fulfilled",
    "orders/paid",
    "orders/partially_fulfilled",
    "orders/updated",
    "payment_terms/create",
    "payment_terms/delete",
    "payment_terms/update",
    "product_listings/add",
    "product_listings/remove",
    "product_listings/update",
    "products/create",
    "products/delete",
    "products/update",
    "refunds/create",
    "selling_plan_groups/create",
    "selling_plan_groups/delete",
    "selling_plan_groups/update",
    "shop/redact",
    "shop/update",
    "subscription_billing_attempts/challenged",
    "subscription_billing_attempts/failure",
    "subscription_billing_attempts/success",
    "subscription_contracts/create",
    "subscription_contracts/update",
    "tender_transactions/create",
    "themes/create",
    "themes/delete",
    "themes/publish",
    "themes/update",
  ],
  "2022-01": ["scheduled_product_listings/add", "scheduled_product_listings/remove", "scheduled_product_listings/update"],
  "2022-04": ["markets/create", "markets/update", "markets/delete"],
  "2023-01": [
    "locations/activate",
    "locations/deactivate",
    "fulfillment_orders/order_routing_complete",
    "fulfillment_orders/fulfillment_request_submitted",
    "fulfillment_orders/fulfillment_request_accepted",
    "fulfillment_orders/fulfillment_request_rejected",
    "fulfillment_orders/placed_on_hold",
    "fulfillment_orders/cancellation_request_accepted",
    "fulfillment_orders/cancellation_request_rejected",
    "fulfillment_orders/cancelled",
    "fulfillment_orders/fulfillment_service_failed_to_complete",
  ],
  "2023-04": [
    "fulfillment_orders/moved",
    "companies/create",
    "companies/update",
    "companies/delete",
    "company_contacts/create",
    "company_contacts/update",
    "company_contacts/delete",
    "company_locations/create",
    "company_locations/update",
    "company_locations/delete",
  ],
  "2023-07": [
    "fulfillment_orders/cancellation_request_submitted",
    "fulfillment_orders/hold_released",
    "fulfillment_orders/line_items_prepared_for_local_delivery",
    "fulfillment_orders/line_items_prepared_for_pickup",
    "fulfillment_orders/rescheduled",
    "fulfillment_orders/scheduled_fulfillment_order_ready",
    "checkouts/delete",
    "collection_listings/add",
    "collection_listings/remove",
    "collection_listings/update",
    "company_contact_roles/assign",
    "company_contact_roles/revoke",
    "customer_groups/create",
    "customer_groups/delete",
    "customer_groups/update",
    "customers/disable",
    "customers/enable",
    "customers/merge",
    "customers_email_marketing_consent/update",
    "customers_marketing_consent/update",
    "locales/create",
    "locales/update",
    "orders/cancelled",
    "orders/edited",
    "orders/fulfilled",
    "orders/paid",
    "orders/partially_fulfilled",
    "payment_schedules/due",
    "product_feeds/full_sync",
    "product_feeds/incremental_sync",
    "product_listings/add",
    "product_listings/remove",
    "product_listings/update",
    "profiles/create",
    "profiles/delete",
    "profiles/update",
    "scheduled_product_listings/add",
    "scheduled_product_listings/remove",
    "scheduled_product_listings/update",
    "subscription_billing_cycle_edits/create",
    "subscription_billing_cycle_edits/delete",
    "subscription_billing_cycle_edits/update",
    "themes/publish",
    "channels/delete",
    "collection_publications/create",
    "collection_publications/delete",
    "collection_publications/update",
    "customer.tags_added",
    "customer.tags_removed",
    "attributed_sessions/first",
    "attributed_sessions/last",
    "product_publications/create",
    "product_publications/delete",
    "product_publications/update",
    "segments/create",
    "segments/delete",
    "segments/update",
    "shipping_addresses/create",
    "shipping_addresses/update",
    "tax_partners/update",
    "tax_services/create",
    "tax_services/update",
    "variants/in_stock",
    "variants/out_of_stock",
    "app_subscriptions/approaching_capped_amount",
    "returns/cancel",
    "returns/close",
    "returns/reopen",
    "returns/request",
    "returns/approve",
    "returns/decline",
    "reverse_deliveries/attach_deliverable",
    "reverse_fulfillment_orders/dispose",
    "payment_terms/create",
    "payment_terms/delete",
    "payment_terms/update",
    "product_feeds/create",
    "product_feeds/update",
    "audit_events/admin_api_activity",
  ],
  "2023-10": ["orders/shopify_protect_eligibility_changed", "publications/delete"],
  "2024-01": [
    "fulfillment_orders/split",
    "fulfillment_orders/merged",
    "discounts/create",
    "discounts/update",
    "discounts/delete",
    "subscription_contracts/activate",
    "subscription_contracts/pause",
    "subscription_contracts/cancel",
    "subscription_contracts/fail",
    "subscription_contracts/expire",
    "subscription_billing_cycles/skip",
    "subscription_billing_cycles/unskip",
    "metaobjects/create",
    "metaobjects/update",
    "metaobjects/delete",
    "discounts/redeemcode_added",
    "discounts/redeemcode_removed",
  ],
  "2024-04": ["orders/risk_assessment_changed", "returns/update"],
  "2024-10": ["metafield_definitions/create", "metafield_definitions/delete", "metafield_definitions/update", "app/scopes_update"],
  "2025-01": ["customers/purchasing_summary"],
}; // satisfies Partial<Record<ShopifyApiVersion, ShopifyWebhookTopic[]>>;   <-- TODO uncomment once fastify-renderer is happy with `satisfies` operator

export const GdprWebhookTopics = ["shop/redact", "customers/redact", "customers/data_request"] as const;

export const webhooksWithoutModels = new Map<ShopifyWebhookTopic, string[]>([
  ["collection_listings/add", ["read_product_listings"]],
  ["collection_listings/remove", ["read_product_listings"]],
  ["collection_listings/update", ["read_product_listings"]],
  ["company_contact_roles/assign", ["read_customers"]],
  ["company_contact_roles/revoke", ["read_customers"]],
  ["customer_groups/create", ["read_customers"]],
  ["customer_groups/delete", ["read_customers"]],
  ["customer_groups/update", ["read_customers"]],
  ["customers_marketing_consent/update", ["read_customers"]],
  ["customers/disable", ["read_customers"]],
  ["customers/enable", ["read_customers"]],
  ["customers/merge", ["read_customers"]],
  ["customers_email_marketing_consent/update", ["read_customers"]],
  ["locales/create", ["read_locales"]],
  ["locales/update", ["read_locales"]],
  ["orders/cancelled", ["read_orders", "read_marketplace_orders", "read_buyer_membership_orders"]],
  ["orders/edited", ["read_orders", "read_marketplace_orders", "read_buyer_membership_orders"]],
  ["orders/fulfilled", ["read_orders", "read_marketplace_orders"]],
  ["orders/paid", ["read_orders", "read_marketplace_orders"]],
  ["orders/partially_fulfilled", ["read_orders", "read_marketplace_orders"]],
  ["payment_schedules/due", ["read_payment_terms"]],
  ["product_feeds/full_sync", ["read_product_listings"]],
  ["product_feeds/incremental_sync", ["read_product_listings"]],
  ["product_listings/add", ["read_product_listings"]],
  ["product_listings/remove", ["read_product_listings"]],
  ["product_listings/update", ["read_product_listings"]],
  ["profiles/create", ["read_shipping", "read_assigned_shipping"]],
  ["profiles/delete", ["read_shipping", "read_assigned_shipping"]],
  ["profiles/update", ["read_shipping", "read_assigned_shipping"]],
  ["scheduled_product_listings/add", ["read_product_listings"]],
  ["scheduled_product_listings/remove", ["read_product_listings"]],
  ["scheduled_product_listings/update", ["read_product_listings"]],
  ["subscription_billing_cycle_edits/create", ["read_own_subscription_contracts"]],
  ["subscription_billing_cycle_edits/delete", ["read_own_subscription_contracts"]],
  ["subscription_billing_cycle_edits/update", ["read_own_subscription_contracts"]],
  ["themes/publish", ["read_themes"]],
  ["channels/delete", ["read_publications"]],
  ["collection_publications/create", ["read_publications"]],
  ["collection_publications/delete", ["read_publications"]],
  ["collection_publications/update", ["read_publications"]],
  ["customer.tags_added", ["read_customers"]],
  ["customer.tags_removed", ["read_customers"]],
  ["attributed_sessions/first", ["read_marketing_events"]],
  ["attributed_sessions/last", ["read_marketing_events"]],
  ["product_publications/create", ["read_publications"]],
  ["product_publications/delete", ["read_publications"]],
  ["product_publications/update", ["read_publications"]],
  ["segments/create", ["read_customers"]],
  ["segments/delete", ["read_customers"]],
  ["segments/update", ["read_customers"]],
  ["shipping_addresses/create", ["read_shipping"]],
  ["shipping_addresses/update", ["read_shipping"]],
  ["tax_partners/update", ["read_taxes"]],
  ["tax_services/create", ["read_taxes"]],
  ["tax_services/update", ["read_taxes"]],
  ["variants/in_stock", ["read_products"]],
  ["variants/out_of_stock", ["read_products"]],
  ["app_subscriptions/approaching_capped_amount", []],
  [
    "returns/cancel",
    ["read_orders", "read_marketplace_orders", "read_returns", "read_marketplace_returns", "read_buyer_membership_orders"],
  ],
  ["returns/close", ["read_orders", "read_marketplace_orders", "read_returns", "read_marketplace_returns", "read_buyer_membership_orders"]],
  [
    "returns/reopen",
    ["read_orders", "read_marketplace_orders", "read_returns", "read_marketplace_returns", "read_buyer_membership_orders"],
  ],
  ["returns/request", ["read_returns", "read_marketplace_returns", "read_buyer_membership_orders"]],
  ["returns/approve", ["read_returns", "read_marketplace_returns", "read_buyer_membership_orders"]],
  ["returns/update", ["read_returns", "read_marketplace_returns", "read_buyer_membership_orders"]],
  ["returns/decline", ["read_returns", "read_marketplace_returns", "read_buyer_membership_orders"]],
  ["reverse_deliveries/attach_deliverable", ["read_returns", "read_marketplace_returns"]],
  ["reverse_fulfillment_orders/dispose", ["read_returns", "read_marketplace_returns"]],
  ["product_feeds/create", ["read_product_listings"]],
  ["product_feeds/update", ["read_product_listings"]],
  ["orders/shopify_protect_eligibility_changed", ["read_orders"]],
  ["publications/delete", ["read_publications"]],
  ["audit_events/admin_api_activity", ["read_audit_events"]],
  ["subscription_contracts/activate", ["read_own_subscription_contracts"]],
  ["subscription_contracts/pause", ["read_own_subscription_contracts"]],
  ["subscription_contracts/cancel", ["read_own_subscription_contracts"]],
  ["subscription_contracts/fail", ["read_own_subscription_contracts"]],
  ["subscription_contracts/expire", ["read_own_subscription_contracts"]],
  ["subscription_billing_cycles/skip", ["read_own_subscription_contracts"]],
  ["subscription_billing_cycles/unskip", ["read_own_subscription_contracts"]],
  ["metaobjects/create", ["read_metaobjects"]],
  ["metaobjects/update", ["read_metaobjects"]],
  ["metaobjects/delete", ["read_metaobjects"]],
  ["discounts/redeemcode_added", ["read_discounts"]],
  ["discounts/redeemcode_removed", ["read_discounts"]],
  ["metafield_definitions/create", ["read_content"]],
  ["metafield_definitions/delete", ["read_content"]],
  ["metafield_definitions/update", ["read_content"]],
  ["app/scopes_update", []],
]);

type _MissingWebhooks = Exclude<ShopifyWebhookTopic, (typeof SHOPIFY_WEBHOOKS)[keyof typeof SHOPIFY_WEBHOOKS][number]>;
type _AllWebhooksCovered = AssertTrue<IsNever<_MissingWebhooks>>;

export const groupedUpdatedWebhooks = (models: string[], currentApiVersion: ShopifyApiVersion, latestApiVersion: ShopifyApiVersion) => {
  const groupedWebhooks = models.reduce((result: Record<string, any>, model) => {
    const modelApiIdentifier = getShopifyModelApiIdentifierByModelKey(model, currentApiVersion);
    const currentMetadata = shopifyModelMetadataForVersion(model, currentApiVersion);
    const latestMetadata = shopifyModelMetadataForVersion(model, latestApiVersion);

    const currentWebhooks =
      currentMetadata?.inheritWebhooks !== true ? (Object.keys(currentMetadata?.webhooks ?? {}) as ShopifyWebhookTopic[]) : [];

    const latestWebhooks =
      latestMetadata?.inheritWebhooks !== true ? (Object.keys(latestMetadata?.webhooks ?? {}) as ShopifyWebhookTopic[]) : [];

    const deletedWebhookTopics = difference<ShopifyWebhookTopic>(currentWebhooks, latestWebhooks).map(
      (webhookTopic) =>
        ({
          topic: webhookTopic,
          contributingScopes: latestMetadata?.contributingScopes,
          modelKey: model,
          parentKey: latestMetadata?.parentKey,
          metaActionIdentifier: latestMetadata?.webhooks?.[webhookTopic]?.metaActionIdentifier,
        } as WebhookApiUpgradeItem)
    );

    const addedWebhookTopics = difference<ShopifyWebhookTopic>(latestWebhooks, currentWebhooks).map(
      (webhookTopic) =>
        ({
          topic: webhookTopic,
          contributingScopes: latestMetadata?.contributingScopes,
          modelKey: model,
          parentKey: latestMetadata?.parentKey,
          metaActionIdentifier: latestMetadata?.webhooks?.[webhookTopic]?.metaActionIdentifier,
        } as WebhookApiUpgradeItem)
    );

    if (addedWebhookTopics.length > 0 || deletedWebhookTopics.length > 0) {
      result[modelApiIdentifier] = { Added: [...addedWebhookTopics], Deleted: [...deletedWebhookTopics] };
    }

    return result;
  }, {});

  return groupedWebhooks;
};
